import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Device, EntityCacheProvider, TransformationService, AGENT_MODEL } from '@nrwl/common-lib';
import {
  IComponentStateHistory,
  IValueChangedEvent,
  IWizardContentComponent
} from '../../../wizard.content';
import { DatasourceWizardService } from '../../../services/datasource/datasource.wizard.service';
import { find } from 'lodash';

@Component({
  selector: 'datasource-agent-upgrade-html',
  templateUrl: './datasource.agent.upgrade.html',
  providers: [],
  styleUrls: [
    '../datasource.style.scss',
    './datasource.agent.upgrade.style.scss',
    '../../../wizard.button.style.scss'
  ]
})
export class DatasourceAgentUpgradeComponent implements IWizardContentComponent, OnInit {
  @Input() step: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() subStep: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm') form: NgForm;
  formatData: { name: string; version: string; icon: string }[];
  upgradeVersion = null;
  selectedAgents: string[];
  deviceList: Array<Device>;
  url: string;
  versionsObj = [];
  availableVersions = [];
  upgradeSelectionTitle: string;
  selectedAgentsVersion = [];
  // eslint-disable-next-line
  agentsForUpgrade: any;
  // eslint-disable-next-line
  jmepProbesForUpgrade = [];
  agentType: string = null;
  upgradeMessage: string;
  constructor(
    private readonly entityCache: EntityCacheProvider,
    private wizardService: DatasourceWizardService,
    private transformService: TransformationService
  ) {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit() {
    const defaultParams = this.step.parent.defaultParamsById;
    this.versionsObj = defaultParams['version'];
    this.selectedAgents = defaultParams['agents'];
    this.agentType = defaultParams['model'];
    this.url = defaultParams['url'];
    if (this.versionsObj) {
      const versionsList = this.versionsObj.map(list => list?.version);
      this.availableVersions = versionsList.sort((a, b) => b - a);
      this.upgradeVersion = this.availableVersions[0];
      this.updateOutputParams();
    }
    this.upgradeMessage =
      this.agentType === AGENT_MODEL.MAP2100 || this.agentType === AGENT_MODEL.QT600
        ? 'The upgrade may take between 20 to 25 minutes to complete'
        : 'The upgrade may take between 5 to 10 minutes to complete';
    const agentString = this.selectedAgents.length > 1 ? 'agents' : 'agent';
    this.upgradeSelectionTitle = 'The following ' + agentString + ' have been selected for upgrade';
    this.entityCache
      .findEntityCache('device')
      .getValuesAsync()
      .pipe(first())
      .subscribe(device => {
        this.deviceList = device;
        this.populateDataForAgentUpgrade();
      });
  }

  populateDataForAgentUpgrade(): void {
    const selectedDevice = [];
    this.selectedAgentsVersion = [];
    this.agentsForUpgrade = [];
    this.selectedAgents.forEach(deviceId => {
      const deviceData = this.getGenericAgentsInfo(deviceId);
      const agentVersion = this.getParamsValueByName(deviceData?.deviceData?.params, 'version');
      this.selectedAgentsVersion.push(agentVersion);
      if (deviceData && this.upgradeVersion !== agentVersion) {
        selectedDevice.push(deviceData);
        if (this.agentType.indexOf('JMEP') !== -1) {
          this.jmepProbesForUpgrade.push(deviceData.deviceData?.capabilities?.phy?.['jmep-id']);
        } else {
          this.agentsForUpgrade.push(deviceData.deviceData.uniqueId);
        }
      }
    });
    if (selectedDevice.length > 1) {
      this.validateAgentsCurrentVersion();
    }
    this.getFormatData(selectedDevice);
    this.updateOutputParams();
  }

  ValidateSelectedAgents(): void {
    this.populateDataForAgentUpgrade();
  }

  getGenericAgentsInfo(agentId: string): any {
    return this.deviceList.find(device => device.getUniqueId() === agentId);
  }

  getJmepsInfo(jmepId: string): any {
    return this.deviceList.find(
      device => device.deviceData?.capabilities?.phy?.['jmep-id'] === jmepId
    );
  }

  updateOutputParams(): void {
    if (this.agentType.indexOf('JMEP') !== -1) {
      const img = this.getupgradingImage(this.upgradeVersion);
      this.onDataEmit('url', this.url, true);
      this.onDataEmit('jmepProbes', this.jmepProbesForUpgrade, true);
      this.onDataEmit('version', img, true);
    } else {
      this.url = this.getupgradeImageURL(this.upgradeVersion);
      this.onDataEmit('agents', this.agentsForUpgrade, true);
      this.onDataEmit('version', this.upgradeVersion, true);
      this.onDataEmit('url', this.url, true);
    }
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  getupgradeImageURL(upgradeVersion: string): string {
    const filteredImg = find(this.versionsObj, ['version', upgradeVersion]);
    return filteredImg?.url || null;
  }

  getupgradingImage(upgradeVersion: string): string {
    const filteredImg = find(this.versionsObj, ['version', upgradeVersion]);
    return filteredImg?.image || null;
  }

  //* Bulk Upgrade when the selected agents are same version remove the current version from the upgrade list  */
  validateAgentsCurrentVersion(): void {
    const versionsList = this.selectedAgentsVersion;
    const uniqueVersions = [...new Set(versionsList)];
    if (uniqueVersions.length === 1) {
      const index = this.availableVersions.indexOf(uniqueVersions[0], 0);
      if (index > -1) {
        this.availableVersions.splice(index, 1);
      }
    }
  }

  // eslint-disable-next-line
  getParamsValueByName(params: any, name: string): any {
    const paramsObj = find(params, ['name', name]);
    return paramsObj && paramsObj.value ? paramsObj.value : '';
  }

  getFormatData(selectedDevice: Device[]): void {
    this.formatData = [];
    selectedDevice.forEach(element => {
      const newObj = {
        name: '',
        version: '',
        icon: ''
      };
      newObj.name = element.deviceData.nameId;
      newObj.version = element.deviceData.model.version;
      newObj.icon = this.transformService.getPlatformIconInfo(
        element.deviceData.params.find(x => x.name == 'platform').value
      ).icon;
      this.formatData.push(newObj);
    });
  }

  isLoading(): boolean {
    return this.wizardCtrl.getWizardService().isLoading;
  }
}
